import React, { useRef, useEffect, useCallback, useState } from "react";

interface ClickSparkProps {
  sparkColor?: string;
  sparkSize?: number;
  sparkRadius?: number;
  sparkCount?: number;
  duration?: number;
  easing?: "linear" | "ease-in" | "ease-out" | "ease-in-out";
  extraScale?: number;
  shape?: "circle" | "heart";
  children?: React.ReactNode;
}

interface Spark {
  x: number;
  y: number;
  angle: number;
  startTime: number;
  color?: string; // Add color property for rainbow effect
  forceShape?: "circle" | "heart"; // Override shape for individual sparks
  sparkSize?: number; // Custom sparkSize for each spark
  sparkRadius?: number; // Custom sparkRadius for each spark
  duration?: number; // Custom duration for each spark
}

// Color palette with all the specified colors
const COLOR_PALETTE = [
  "#f94144", // Imperial red
  "#f3722c", // Orange (Crayola)
  "#f8961e", // Carrot orange
  "#f9844a", // Coral
  "#f9c74f", // Saffron
  "#90be6d", // Pistachio
  "#43aa8b", // Zomp
  "#4d908e", // Dark cyan
  "#577590", // Payne's gray
  "#277da1", // Cerulean
];

// Settings for the first two clicks
const FIRST_CLICKS_SETTINGS = {
  sparkSize: 15,
  sparkRadius: 25,
  sparkCount: 8,
  duration: 400,
};

const ClickSpark: React.FC<ClickSparkProps> = ({
  sparkColor = "#fff",
  sparkSize = 10,
  sparkRadius = 15,
  sparkCount = 8,
  duration = 400,
  easing = "ease-out",
  extraScale = 1.0,
  shape = "circle",
  children,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sparksRef = useRef<Spark[]>([]); // Stores spark data
  const startTimeRef = useRef<number | null>(null); // Tracks initial timestamp for animation
  const [totalClickCount, setTotalClickCount] = useState<number>(0); // Track total clicks
  const [heartClickCount, setHeartClickCount] = useState<number>(0); // Track heart clicks for easter egg
  const [colorIndex, setColorIndex] = useState<number>(0); // Track which color to use

  // Heart shape parametric function
  const heartShape = useCallback((angle: number, scale: number) => {
    // Convert angle to parameter t (0 to 2π)
    const t = angle;
    
    // Parametric equations for heart shape
    const x = 16 * Math.pow(Math.sin(t), 3);
    const y = 13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t);
    
    // Scale and return coordinates
    return {
      x: x * scale,
      y: -y * scale, // Negative to invert Y axis (canvas Y is inverted)
    };
  }, []);

  // Create giant rainbow heart using our color palette
  const createRainbowHeart = useCallback((x: number, y: number) => {
    const now = performance.now();
    const rainbowSparkCount = COLOR_PALETTE.length * 3; // 3 sparks per color for a smoother heart
    const rainbowSparks: Spark[] = Array.from({ length: rainbowSparkCount }, (_, i) => {
      const angle = (2 * Math.PI * i) / rainbowSparkCount;
      // Get color from palette, distribute them evenly around the heart
      const colorIdx = Math.floor((i * COLOR_PALETTE.length) / rainbowSparkCount);
      return {
        x,
        y,
        angle,
        startTime: now,
        color: COLOR_PALETTE[colorIdx % COLOR_PALETTE.length], // Use our color palette
        forceShape: "heart", // Always use heart shape for rainbow sparks
        duration: duration * 3, // Easter egg lasts longer
        sparkRadius: sparkRadius * 4, // Easter egg is bigger
        sparkSize: sparkSize * 2 // Easter egg spark lines are longer
      };
    });
    
    sparksRef.current.push(...rainbowSparks);
  }, [duration, sparkRadius, sparkSize]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const parent = canvas.parentElement;
    if (!parent) return;

    let resizeTimeout: NodeJS.Timeout;

    const resizeCanvas = () => {
      const { width, height } = parent.getBoundingClientRect();
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
    };

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(resizeCanvas, 100); // Debounce by 100ms
    };

    // Observe size changes
    const ro = new ResizeObserver(handleResize);
    ro.observe(parent);

    // Initial sizing
    resizeCanvas();

    // Cleanup
    return () => {
      ro.disconnect();
      clearTimeout(resizeTimeout);
    };
  }, []);

  const easeFunc = useCallback(
    (t: number) => {
      switch (easing) {
        case "linear":
          return t;
        case "ease-in":
          return t * t;
        case "ease-in-out":
          return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
        default:
          return t * (2 - t);
      }
    },
    [easing],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationId: number;

    const draw = (timestamp: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp; // store initial time
      }
      ctx?.clearRect(0, 0, canvas.width, canvas.height);

      sparksRef.current = sparksRef.current.filter((spark: Spark) => {
        const elapsed = timestamp - spark.startTime;
        // Use spark's custom duration if provided, otherwise use the prop
        const sparkDuration = spark.duration || duration;
        
        if (elapsed >= sparkDuration) {
          // Spark finished its animation
          return false;
        }

        const progress = elapsed / sparkDuration;
        const eased = easeFunc(progress);

        // Use spark's custom properties if provided, otherwise use props
        const actualSparkRadius = spark.sparkRadius || sparkRadius;
        const actualSparkSize = spark.sparkSize || sparkSize;
        
        const distance = eased * actualSparkRadius * extraScale;
        const lineLength = actualSparkSize * (1 - eased);

        let x1, y1, x2, y2;

        // Determine shape - use spark's forceShape if specified, otherwise use component shape
        const currentShape = spark.forceShape || shape;

        if (currentShape === "heart") {
          // Get heart coordinates based on angle
          const heartScale = distance / 16; // Scale factor to match sparkRadius
          const point1 = heartShape(spark.angle, heartScale);
          const point2 = heartShape(spark.angle, heartScale + lineLength / 16);
          
          // Points for the spark line on heart shape
          x1 = spark.x + point1.x;
          y1 = spark.y + point1.y;
          x2 = spark.x + point2.x;
          y2 = spark.y + point2.y;
        } else {
          // Circle shape (original code)
          x1 = spark.x + distance * Math.cos(spark.angle);
          y1 = spark.y + distance * Math.sin(spark.angle);
          x2 = spark.x + (distance + lineLength) * Math.cos(spark.angle);
          y2 = spark.y + (distance + lineLength) * Math.sin(spark.angle);
        }

        // Draw the spark line
        ctx.strokeStyle = spark.color || sparkColor;
        ctx.lineWidth = spark.forceShape === "heart" && spark.color ? 3 : 2; // Thicker lines for rainbow heart
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();

        return true;
      });

      animationId = requestAnimationFrame(draw);
    };

    animationId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [
    sparkColor,
    sparkSize,
    sparkRadius,
    sparkCount,
    duration,
    easeFunc,
    extraScale,
    shape,
    heartShape,
  ]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Increment total click count
    const newTotalCount = totalClickCount + 1;
    setTotalClickCount(newTotalCount);

    // Update color index for next click
    const nextColorIndex = (colorIndex + 1) % COLOR_PALETTE.length;
    setColorIndex(nextColorIndex);
    
    // Current color from the palette
    const currentColor = COLOR_PALETTE[colorIndex];

    // If we're using heart shape and we're past the first 2 clicks
    if (shape === "heart" && newTotalCount > 2) {
      const newHeartCount = heartClickCount + 1;
      setHeartClickCount(newHeartCount);
      
      // Easter egg: on 5th heart click, create rainbow heart
      if (newHeartCount === 5) {
        createRainbowHeart(x, y);
        setHeartClickCount(0); // Reset counter
        return;
      }
    }

    const now = performance.now();
    
    // Determine which shape to use for sparks
    // First 2 clicks always use circle, even if shape is set to "heart"
    const sparkShape = (shape === "heart" && newTotalCount > 2) ? "heart" : "circle";
    
    // Determine which settings to use
    // For first 2 clicks, use FIRST_CLICKS_SETTINGS, otherwise use component props
    const isFirstTwoClicks = newTotalCount <= 2;
    const actualSparkCount = isFirstTwoClicks ? FIRST_CLICKS_SETTINGS.sparkCount : sparkCount;
    
    const newSparks: Spark[] = Array.from({ length: actualSparkCount }, (_, i) => {
      const spark: Spark = {
        x,
        y,
        angle: (2 * Math.PI * i) / actualSparkCount,
        startTime: now,
        forceShape: sparkShape,
        color: currentColor // Use current color from palette
      };
      
      // Apply special settings for first two clicks
      if (isFirstTwoClicks) {
        spark.sparkSize = FIRST_CLICKS_SETTINGS.sparkSize;
        spark.sparkRadius = FIRST_CLICKS_SETTINGS.sparkRadius;
        spark.duration = FIRST_CLICKS_SETTINGS.duration;
      }
      
      return spark;
    });

    sparksRef.current.push(...newSparks);
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
      }}
      onClick={handleClick}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
        }}
      />
      {children}
    </div>
  );
};

export default ClickSpark;
